import { useEffect, useState, type ReactNode } from 'react';
import { api, type SettingItem } from '@/api/client';
import { ErrorBox, Loading, PageTitle, fullW } from '@/components/ui';

export function SettingsPage() {
  const [items, setItems] = useState<SettingItem[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [edits, setEdits] = useState<Record<string, unknown>>({});
  const [saving, setSaving] = useState<string | null>(null);

  const load = () =>
    api
      .settings()
      .then(({ items }) => setItems(items))
      .catch((e) => setErr(String(e)));

  useEffect(() => {
    void load();
  }, []);

  if (err) return <ErrorBox text={err} />;
  if (!items) return <Loading />;

  const save = async (key: string) => {
    setSaving(key);
    setErr(null);
    try {
      const raw = key in edits ? edits[key] : items.find((i) => i.key === key)?.value;
      await api.setSetting(key, raw);
      await load();
      setEdits((e) => {
        const next = { ...e };
        delete next[key];
        return next;
      });
    } catch (e) {
      setErr(String(e));
    } finally {
      setSaving(null);
    }
  };

  const reset = async (item: SettingItem) => {
    setSaving(item.key);
    try {
      await api.setSetting(item.key, item.defaultValue);
      await load();
    } finally {
      setSaving(null);
    }
  };

  const byCat = items.reduce<Record<string, SettingItem[]>>((acc, it) => {
    (acc[it.category] ??= []).push(it);
    return acc;
  }, {});
  const appItems = byCat.app ?? [];
  delete byCat.app;

  const saveAppControl = async () => {
    const dirtyItems = appItems.filter((item) => item.key in edits);
    if (dirtyItems.length === 0) return;
    const maintenanceFlag = dirtyItems.find(
      (item) => item.key === 'app.maintenance.enabled',
    );
    const remainingItems = dirtyItems.filter(
      (item) => item.key !== 'app.maintenance.enabled',
    );
    // When enabling maintenance, persist the bypass list/message first so
    // connected testers are never evaluated against stale values. When
    // disabling it, lift the gate first.
    const orderedItems =
      maintenanceFlag && edits[maintenanceFlag.key] === false
        ? [maintenanceFlag, ...remainingItems]
        : maintenanceFlag
          ? [...remainingItems, maintenanceFlag]
          : remainingItems;
    setSaving('app-control');
    setErr(null);
    try {
      for (const item of orderedItems) {
        let value = edits[item.key];
        if (item.key === 'app.maintenance.testAccounts' && Array.isArray(value)) {
          value = [...new Set(
            value
              .filter((entry): entry is string => typeof entry === 'string')
              .map((entry) => entry.trim())
              .filter(Boolean),
          )];
        }
        await api.setSetting(item.key, value);
      }
      await load();
      setEdits((current) => {
        const next = { ...current };
        for (const item of dirtyItems) delete next[item.key];
        return next;
      });
    } catch (e) {
      setErr(String(e));
    } finally {
      setSaving(null);
    }
  };

  return (
    <div>
      <PageTitle title="Game Config" />

      <div
        className="card"
        style={{
          padding: '10px 14px',
          marginBottom: 16,
          fontSize: 12,
          color: 'var(--ink-muted)',
          background: 'rgba(34,211,238,0.08)',
          borderColor: 'rgba(34,211,238,0.25)',
        }}
      >
        Edits hit the database immediately. The server cache TTL is ~30s, so
        new matches will pick up your change within that window. Active matches
        keep their original values until they finish.
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {appItems.length > 0 && (
          <AppControlPanel
            items={appItems}
            edits={edits}
            saving={saving === 'app-control'}
            onChange={(key, value) => setEdits((current) => ({ ...current, [key]: value }))}
            onSave={() => void saveAppControl()}
          />
        )}
        {Object.entries(byCat).map(([cat, list]) => (
          <div key={cat} className="card" style={{ padding: 16 }}>
            <div className="tag" style={{ color: 'var(--c-cyan)', marginBottom: 12 }}>
              {cat.toUpperCase()}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {list.map((it, idx) => {
                const current = it.key in edits ? edits[it.key] : it.value;
                const dirty = it.key in edits && edits[it.key] !== it.value;
                return (
                  <div
                    key={it.key}
                    style={{
                      borderTop: idx === 0 ? 'none' : '1px solid var(--hairline)',
                      paddingTop: idx === 0 ? 0 : 12,
                      paddingBottom: 12,
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'baseline',
                        marginBottom: 4,
                      }}
                    >
                      <span className="mono" style={{ fontSize: 12 }}>{it.key}</span>
                      <span className="tag" style={{ fontSize: 9 }}>
                        default: {String(it.defaultValue)}
                      </span>
                    </div>
                    {it.description && (
                      <div style={{ fontSize: 12, color: 'var(--ink-muted)', marginBottom: 6 }}>
                        {it.description}
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: 6 }}>
                      <SettingInput
                        item={it}
                        value={current}
                        onChange={(v) => setEdits((e) => ({ ...e, [it.key]: v }))}
                      />
                      <button
                        className="btn btn-primary"
                        onClick={() => save(it.key)}
                        disabled={!dirty || saving === it.key}
                        style={{ opacity: dirty ? 1 : 0.4 }}
                      >
                        {saving === it.key ? '…' : 'SAVE'}
                      </button>
                      <button className="btn" onClick={() => reset(it)} title="Reset to default">
                        RESET
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const APP_KEYS = {
  announcementEnabled: 'app.announcement.enabled',
  announcementTitle: 'app.announcement.title',
  announcementMessage: 'app.announcement.message',
  maintenanceEnabled: 'app.maintenance.enabled',
  maintenanceMessage: 'app.maintenance.message',
  maintenanceTestAccounts: 'app.maintenance.testAccounts',
} as const;

function AppControlPanel({
  items,
  edits,
  saving,
  onChange,
  onSave,
}: {
  items: SettingItem[];
  edits: Record<string, unknown>;
  saving: boolean;
  onChange: (key: string, value: unknown) => void;
  onSave: () => void;
}) {
  const itemMap = new Map(items.map((item) => [item.key, item]));
  const value = (key: string): unknown =>
    key in edits ? edits[key] : itemMap.get(key)?.value;
  const dirty = items.some((item) => item.key in edits);
  const maintenanceEnabled = Boolean(value(APP_KEYS.maintenanceEnabled));
  const testAccounts = value(APP_KEYS.maintenanceTestAccounts);

  return (
    <div
      className="card"
      style={{
        padding: 18,
        borderColor: maintenanceEnabled
          ? 'rgba(230,57,70,0.55)'
          : 'rgba(34,211,238,0.3)',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          marginBottom: 16,
        }}
      >
        <div>
          <div className="display" style={{ fontSize: 16, color: 'var(--c-cyan)' }}>
            APP CONTROL
          </div>
          <div style={{ marginTop: 5, color: 'var(--ink-muted)', fontSize: 12 }}>
            Thông báo khi mở app và chế độ bảo trì toàn hệ thống.
          </div>
        </div>
        <span
          className="badge"
          style={{
            flexShrink: 0,
            background: maintenanceEnabled
              ? 'rgba(230,57,70,0.2)'
              : 'rgba(74,222,128,0.14)',
            color: maintenanceEnabled ? 'var(--c-red)' : '#4ade80',
          }}
        >
          {maintenanceEnabled ? 'MAINTENANCE ON' : 'LIVE'}
        </span>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 300px), 1fr))',
          gap: 14,
        }}
      >
        <section
          style={{
            padding: 14,
            border: '1px solid var(--hairline)',
            borderRadius: 12,
            background: 'rgba(34,211,238,0.05)',
          }}
        >
          <div className="tag" style={{ color: 'var(--c-cyan)', marginBottom: 12 }}>
            OPEN-APP ANNOUNCEMENT
          </div>
          <AppField label="Hiển thị thông báo">
            <select
              value={String(Boolean(value(APP_KEYS.announcementEnabled)))}
              onChange={(event) =>
                onChange(APP_KEYS.announcementEnabled, event.target.value === 'true')
              }
              style={fullW}
            >
              <option value="false">Tắt</option>
              <option value="true">Bật</option>
            </select>
          </AppField>
          <AppField label="Tiêu đề">
            <input
              value={String(value(APP_KEYS.announcementTitle) ?? '')}
              onChange={(event) => onChange(APP_KEYS.announcementTitle, event.target.value)}
              maxLength={120}
              style={fullW}
            />
          </AppField>
          <AppField label="Nội dung">
            <textarea
              rows={6}
              value={String(value(APP_KEYS.announcementMessage) ?? '')}
              onChange={(event) => onChange(APP_KEYS.announcementMessage, event.target.value)}
              placeholder="Nội dung sẽ hiện một lần mỗi khi người chơi mở app."
              style={{ ...fullW, resize: 'vertical' }}
            />
          </AppField>
        </section>

        <section
          style={{
            padding: 14,
            border: `1px solid ${
              maintenanceEnabled ? 'rgba(230,57,70,0.45)' : 'var(--hairline)'
            }`,
            borderRadius: 12,
            background: maintenanceEnabled
              ? 'rgba(230,57,70,0.08)'
              : 'rgba(255,255,255,0.02)',
          }}
        >
          <div className="tag" style={{ color: 'var(--c-red)', marginBottom: 12 }}>
            MAINTENANCE MODE
          </div>
          <AppField label="Trạng thái">
            <select
              value={String(maintenanceEnabled)}
              onChange={(event) =>
                onChange(APP_KEYS.maintenanceEnabled, event.target.value === 'true')
              }
              style={fullW}
            >
              <option value="false">Tắt — app hoạt động bình thường</option>
              <option value="true">Bật — chặn toàn bộ người chơi</option>
            </select>
          </AppField>
          <AppField label="Thông báo bảo trì">
            <textarea
              rows={3}
              value={String(value(APP_KEYS.maintenanceMessage) ?? '')}
              onChange={(event) => onChange(APP_KEYS.maintenanceMessage, event.target.value)}
              style={{ ...fullW, resize: 'vertical' }}
            />
          </AppField>
          <AppField
            label="Tài khoản test được truy cập"
            hint="Mỗi dòng là một email, username hoặc user ID. Server xác thực trực tiếp từ tài khoản đăng nhập."
          >
            <textarea
              rows={5}
              value={
                Array.isArray(testAccounts)
                  ? testAccounts.map((entry) => String(entry)).join('\n')
                  : ''
              }
              onChange={(event) =>
                onChange(APP_KEYS.maintenanceTestAccounts, event.target.value.split('\n'))
              }
              placeholder={'tester@dontmiss.games\ntester_username'}
              spellCheck={false}
              style={{ ...fullW, resize: 'vertical', fontFamily: 'var(--f-mono)' }}
            />
          </AppField>
          {maintenanceEnabled && (
            <div
              style={{
                marginTop: 10,
                padding: '9px 10px',
                borderRadius: 8,
                background: 'rgba(230,57,70,0.14)',
                color: 'var(--c-red)',
                fontSize: 12,
                lineHeight: 1.45,
              }}
            >
              Khi lưu, mọi tài khoản không nằm trong danh sách test sẽ bị chặn ở màn hình bảo trì.
            </div>
          )}
        </section>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 14 }}>
        <button
          className={maintenanceEnabled ? 'btn btn-danger' : 'btn btn-primary'}
          onClick={onSave}
          disabled={!dirty || saving}
        >
          {saving ? 'SAVING…' : 'SAVE APP CONTROL'}
        </button>
      </div>
    </div>
  );
}

function AppField({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 11 }}>
      <span style={{ fontSize: 12, fontWeight: 700 }}>{label}</span>
      {children}
      {hint && (
        <span style={{ color: 'var(--ink-dim)', fontSize: 11, lineHeight: 1.4 }}>
          {hint}
        </span>
      )}
    </label>
  );
}

function SettingInput({
  item,
  value,
  onChange,
}: {
  item: SettingItem;
  value: unknown;
  onChange: (v: unknown) => void;
}) {
  if (item.type === 'boolean') {
    return (
      <select
        value={String(Boolean(value))}
        onChange={(e) => onChange(e.target.value === 'true')}
        style={fullW}
      >
        <option value="true">true</option>
        <option value="false">false</option>
      </select>
    );
  }
  if (item.type === 'integer' || item.type === 'number') {
    return (
      <input
        type="number"
        value={value as number}
        min={item.min}
        max={item.max}
        // 0.1 step for floats lets admins set values like 8.5s without the
        // browser snapping to integers (default step=1). Integers stay snapped.
        step={item.type === 'integer' ? 1 : 0.1}
        onChange={(e) => {
          const raw = e.target.value;
          if (raw === '') return;
          const parsed = item.type === 'integer' ? parseInt(raw, 10) : parseFloat(raw);
          if (!Number.isFinite(parsed)) return;
          // Snap floats to 1-decimal precision so the stored value isn't 8.299999.
          const snapped = item.type === 'integer' ? parsed : Math.round(parsed * 10) / 10;
          onChange(snapped);
        }}
        style={fullW}
      />
    );
  }
  if (item.type === 'string') {
    return <input value={value as string} onChange={(e) => onChange(e.target.value)} style={fullW} />;
  }
  return (
    <textarea
      rows={3}
      value={JSON.stringify(value, null, 2)}
      onChange={(e) => {
        try {
          onChange(JSON.parse(e.target.value));
        } catch {
          /* */
        }
      }}
      style={{ ...fullW, fontFamily: 'var(--f-mono)', resize: 'vertical' }}
    />
  );
}
